import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import RegionMunicipalityModal from './RegionMunicipalityModal';
import TownModal from './TownModal';
import DeleteModal from '@shared/components/DeleteModal';
import CountryModal from './CountryModal';
import { useRegions } from '@modules/additionalSettings/regions/hooks/useRegions';

export default function Regions() {
    const { t } = useTranslation();
    const {
        selectedRegion,
        selectedMunicipality,
        selectedTown,
        selectedCountry,
        countries,
        regions,
        municipalities,
        towns,
        modalState,
        deleteModalState,
        handleCountryChange,
        handleRegionSelect,
        handleMunicipalitySelect,
        handleTownSelect,
        handleOpenModal,
        handleCloseModal,
        handleConfirm,
        handleOpenDeleteModal,
        handleCloseDeleteModal,
        handleConfirmDelete,
        fetchCountries
    } = useRegions();

    return (
        <div className="regions-wrapper">
            <h1 className="main-heading">{t('regions.title')}</h1>
            
            <div className="select-container">
                <div className="country-header">
                    <div className="country-select-wrapper">
                        <Select
                            value={selectedCountry}
                            onChange={handleCountryChange}
                            options={countries.map(country => ({
                                value: country.countryCode,
                                label: country.countryNameBg
                            }))}
                            placeholder={t('regions.countrySelect')}
                            className="country-select"
                            isClearable
                            formatOptionLabel={({ value, label }) => (
                                <div className="country-option">
                                    <span className="country-code">{value}</span>
                                    <span className="country-name">{label}</span>
                                </div>
                            )}
                        />
                    </div>
                    <div className="actions">
                        <FontAwesomeIcon 
                            icon={faPlus} 
                            className="icon" 
                            title={t('common.add')}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenModal('country', 'add');
                            }}
                        />
                        <FontAwesomeIcon 
                            icon={faEdit} 
                            className={`icon ${!selectedCountry ? 'disabled' : ''}`}
                            title={t('common.edit')}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (selectedCountry) {
                                    const latestCountryData = countries.find(c => c.countryCode === selectedCountry.value);
                                    if (latestCountryData) {
                                        handleOpenModal('country', 'edit', latestCountryData);
                                    }
                                }
                            }}
                        />
                        <FontAwesomeIcon 
                            icon={faTrash} 
                            className={`icon ${!selectedCountry ? 'disabled' : ''}`}
                            title={t('common.remove')}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (selectedCountry) {
                                    const countryData = countries.find(c => c.countryCode === selectedCountry.value);
                                    handleOpenDeleteModal('country', countryData);
                                }
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="regions-container">
                <div className="column">
                    <div className="column-header">
                        <h2>{t('regions.areas')}</h2>
                        <div className="actions">
                            <FontAwesomeIcon 
                                icon={faPlus} 
                                className="icon" 
                                title={t('common.add')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenModal('region', 'add');
                                }}
                            />
                            <FontAwesomeIcon 
                                icon={faEdit} 
                                className={`icon ${!selectedRegion ? 'disabled' : ''}`}
                                title={t('common.edit')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    selectedRegion && handleOpenModal('region', 'edit', selectedRegion);
                                }}
                            />
                            <FontAwesomeIcon 
                                icon={faTrash} 
                                className={`icon ${!selectedRegion ? 'disabled' : ''}`}
                                title={t('common.remove')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    selectedRegion && handleOpenDeleteModal('region', selectedRegion);
                                }}
                            />
                        </div>
                    </div>
                    <div className="list">
                        <table>
                            <thead>
                                <tr>
                                    <th className="country-col">{t('regions.country')}</th>
                                    <th className="name-col">{t('regions.name')}</th>
                                    <th className="transliteration-col">{t('regions.transliteration')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {regions.map(region => (
                                    <tr
                                        key={region.id}
                                        className={selectedRegion?.id === region.id ? 'selected' : ''}
                                        onClick={() => handleRegionSelect(region)}
                                    >
                                        <td className="country-col">
                                            <span className="country-badge">
                                                {selectedCountry ? selectedCountry.value : ''}
                                            </span>
                                        </td>
                                        <td className="name-col">{region.name}</td>
                                        <td className="transliteration-col">{region.transliteration || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="column">
                    <div className="column-header">
                        <h2>{t('regions.municipalities')}</h2>
                        <div className="actions">
                            <FontAwesomeIcon 
                                icon={faPlus} 
                                className={`icon ${!selectedRegion ? 'disabled' : ''}`}
                                title={t('common.add')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    selectedRegion && handleOpenModal('municipality', 'add');
                                }}
                            />
                            <FontAwesomeIcon 
                                icon={faEdit} 
                                className={`icon ${!selectedMunicipality ? 'disabled' : ''}`}
                                title={t('common.edit')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    selectedMunicipality && handleOpenModal('municipality', 'edit', selectedMunicipality);
                                }}
                            />
                            <FontAwesomeIcon 
                                icon={faTrash} 
                                className={`icon ${!selectedMunicipality ? 'disabled' : ''}`}
                                title={t('common.remove')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    selectedMunicipality && handleOpenDeleteModal('municipality', selectedMunicipality);
                                }}
                            />
                        </div>
                    </div>
                    <div className="list">
                        <table>
                            <thead>
                                <tr>
                                    <th className="name-col">{t('regions.name')}</th>
                                    <th className="transliteration-col">{t('regions.transliteration')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {municipalities.map(municipality => (
                                    <tr
                                        key={municipality.id}
                                        className={selectedMunicipality?.id === municipality.id ? 'selected' : ''}
                                        onClick={() => handleMunicipalitySelect(municipality)}
                                    >
                                        <td className="name-col">{municipality.name}</td>
                                        <td className="transliteration-col">{municipality.transliteration || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="column">
                    <div className="column-header">
                        <h2>{t('regions.towns')}</h2>
                        <div className="actions">
                            <FontAwesomeIcon 
                                icon={faPlus} 
                                className={`icon ${!selectedMunicipality ? 'disabled' : ''}`}
                                title={t('common.add')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    selectedMunicipality && handleOpenModal('town', 'add');
                                }}
                            />
                            <FontAwesomeIcon 
                                icon={faEdit} 
                                className={`icon ${!selectedTown ? 'disabled' : ''}`}
                                title={t('common.edit')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    selectedTown && handleOpenModal('town', 'edit', selectedTown);
                                }}
                            />
                            <FontAwesomeIcon 
                                icon={faTrash} 
                                className={`icon ${!selectedTown ? 'disabled' : ''}`}
                                title={t('common.remove')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    selectedTown && handleOpenDeleteModal('town', selectedTown);
                                }}
                            />
                        </div>
                    </div>
                    <div className="list">
                        <table>
                            <thead>
                                <tr>
                                    <th className="zip-col">{t('regions.postcode')}</th>
                                    <th className="type-col">{t('regions.type')}</th>
                                    <th className="name-col">{t('regions.name')}</th>
                                    <th className="transliteration-col">{t('regions.transliteration')}</th>
                                    <th className="ucattu-col">{t('regions.ekatte')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {towns.map(town => (
                                    <tr
                                        key={town.id}
                                        className={selectedTown?.id === town.id ? 'selected' : ''}
                                        onClick={() => handleTownSelect(town)}
                                    >
                                        <td className="zip-col">{town.postal}</td>
                                        <td className="type-col">{town.cityType}</td>
                                        <td className="name-col">{town.name}</td>
                                        <td className="transliteration-col">{town.transliteration || ''}</td>
                                        <td className="ucattu-col">{town.ekatte}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {modalState.visible && (
                <>
                    {modalState.type === 'country' && (
                        <CountryModal
                            onClose={handleCloseModal}
                            onConfirm={handleConfirm}
                            editData={modalState.editData}
                        />
                    )}
                    {modalState.type !== 'town' && modalState.type !== 'country' && (
                        <RegionMunicipalityModal
                            onClose={handleCloseModal}
                            onConfirm={handleConfirm}
                            type={modalState.type}
                            editData={modalState.editData}
                            selectedCountry={selectedCountry}
                            selectedRegion={selectedRegion}
                        />
                    )}
                    {modalState.type === 'town' && (
                        <TownModal
                            onClose={handleCloseModal}
                            onConfirm={handleConfirm}
                            editData={modalState.editData}
                            selectedCountry={selectedCountry}
                            selectedRegion={selectedRegion}
                            selectedMunicipality={selectedMunicipality}
                        />
                    )}
                </>
            )}

            {deleteModalState.visible && (
                <DeleteModal
                    onClose={handleCloseDeleteModal}
                    onConfirm={handleConfirmDelete}
                    name={deleteModalState.data?.name || ''}
                    entityType={deleteModalState.type}
                />
            )}
        </div>
    );
}